import { humanize } from "./textHelpers";

// ============================================================
// INSIGHT DETECTION ENGINE
// ============================================================
// Turns verified metrics into structured findings. Every FACT string here
// is built deterministically from metricsEngine's numbers — never invented,
// never phrased by an LLM. Only the (separate) narrative layer is allowed
// to add business-interpretation prose on TOP of these facts, and it is
// instructed to never contradict or restate a different number.
//
// Findings carry an explicit evidenceLevel so the narrative layer knows
// how confidently it's allowed to write about each one:
//   "fact"        — directly observed in the data, state plainly
//   "correlation" — two things moved together, do not imply cause
//   "hypothesis"  — a plausible but unconfirmed explanation

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "N/A";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function pct(n) {
  if (n === null || n === undefined || isNaN(n)) return "N/A";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

export function buildFindings(metrics) {

  const findings = [];
  const measureName = metrics.primary?.measure;

  // ---- 1. Overall trend ----
  if (metrics.overallChange && measureName) {

    const { direction, pctChange, firstPeriod, lastPeriod } = metrics.overallChange;

    const verbed = direction === "increased" ? "increased" : direction === "decreased" ? "declined" : "remained stable";

    findings.push({
      id: "overall_trend",
      type: "trend",
      evidenceLevel: "fact",
      title: `${measureName} ${verbed}${direction !== "stable" ? ` (${pct(pctChange)})` : ""}`,
      fact: direction === "stable"
        ? `Total ${measureName} remained approximately stable between ${firstPeriod} and ${lastPeriod}.`
        : `Total ${measureName} ${verbed} by ${Math.abs(pctChange).toFixed(1)}% between ${firstPeriod} and ${lastPeriod}.`,
      priority: 1,
    });
  }

  // ---- 2. Count vs. average decomposition of the trend (causality guard) ----
  // This is the deterministic answer to "was the change driven by volume or
  // by transaction size" — computed here so the narrative layer never has
  // to guess or overclaim.
  if (metrics.countSeries?.length >= 2 && metrics.averageSeries?.length >= 2 && metrics.overallChange) {

    const c0 = metrics.countSeries[0].value;
    const c1 = metrics.countSeries[metrics.countSeries.length - 1].value;
    const a0 = metrics.averageSeries[0].value;
    const a1 = metrics.averageSeries[metrics.averageSeries.length - 1].value;

    const countChangePct = c0 ? ((c1 - c0) / c0) * 100 : null;
    const avgChangePct = a0 ? ((a1 - a0) / a0) * 100 : null;

    if (metrics.overallChange.direction !== "stable" && countChangePct !== null && avgChangePct !== null) {

      findings.push({
        id: "trend_decomposition",
        type: "trend_decomposition",
        evidenceLevel: "correlation",
        title: "Change coincides with volume and transaction-size shifts",
        fact: `Over the same period, transaction count changed ${pct(countChangePct)} and average transaction value changed ${pct(avgChangePct)}.`,
        caution: "The available dimensions do not establish which factor, if either, caused the overall change. Do not state a cause — describe this as a coincidence in timing only.",
        priority: 4,
      });
    }
  }

  // ---- 3. Representative / person concentration ----
  if (metrics.repPerformance && measureName) {

    const { concentration, rows, dimension } = metrics.repPerformance;
    const dimensionLabel = humanize(dimension);
    const dimensionLower = dimensionLabel.toLowerCase();
    const topN = Math.min(3, concentration.entityCount);

    findings.push({
      id: "rep_concentration",
      type: "concentration",
      evidenceLevel: "fact",
      title: `Performance is concentrated among ${concentration.entityCount} ${dimensionLower}${concentration.entityCount === 1 ? "" : "s"}`,
      fact: `The top ${topN} ${dimensionLower}${topN === 1 ? "" : "s"} account for ${fmt(topN === 1 ? concentration.top1Share : topN === 2 ? concentration.top2Share : concentration.top3Share)}% of total ${measureName}.`,
      lowSampleCaution: concentration.entityCount <= 5,
      priority: 2,
    });

    if (rows.length >= 2) {

      findings.push({
        id: "rep_gap",
        type: "gap",
        evidenceLevel: "fact",
        title: `${rows[0].label} leads ${dimensionLabel} performance`,
        fact: `${rows[0].label} contributed ${fmt(rows[0].share)}% of total ${measureName}, ${fmt(Math.abs(rows[1].diffFromTop))}% ahead of ${rows[1].label}.`,
        priority: 5,
      });
    }
  }

  // ---- 4. Additional dimension concentration (only the strongest one) ----
  if (metrics.dimensionBreakdowns?.length && measureName) {

    const top = metrics.dimensionBreakdowns[0];
    const topRow = top.rows[0];
    const dimLabel = humanize(top.dimension);

    if (topRow.share >= 20) {

      findings.push({
        id: `dimension_${top.dimension}`,
        type: "dimension",
        evidenceLevel: "fact",
        title: `${topRow.label} leads ${dimLabel}`,
        fact: `${topRow.label} accounts for ${fmt(topRow.share)}% of total ${measureName} across all ${dimLabel.toLowerCase()} values.`,
        priority: 6,
      });
    }
  }

  // ---- 5. Measure correlation (supporting evidence only) ----
  if (metrics.measureCorrelation && (metrics.measureCorrelation.strength === "strong" || metrics.measureCorrelation.strength === "moderate")) {

    const c = metrics.measureCorrelation;

    findings.push({
      id: "measure_correlation",
      type: "correlation",
      evidenceLevel: "correlation",
      title: `${c.columnX} and ${c.columnY} move together`,
      fact: `A ${c.strength} ${c.direction} correlation (coefficient ${c.coefficient}) was observed between ${c.columnX} and ${c.columnY}.`,
      caution: "Correlation does not establish which variable, if either, drives the other.",
      priority: 7,
    });
  }

  // ---- 6. Data quality ----
  if (metrics.dataQuality.missingValueColumns.length) {

    const worst = [...metrics.dataQuality.missingValueColumns].sort((a, b) => b.missingPercent - a.missingPercent)[0];

    findings.push({
      id: "data_quality",
      type: "data_quality",
      evidenceLevel: "fact",
      title: "Incomplete data in one or more fields",
      fact: `${worst.column} is missing in ${fmt(worst.missingPercent)}% of records.${metrics.dataQuality.missingValueColumns.length > 1 ? ` ${metrics.dataQuality.missingValueColumns.length - 1} additional field(s) also have missing values.` : ""}`,
      priority: 8,
    });
  }

  return findings.sort((a, b) => a.priority - b.priority);
}
